if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          // Don't change the page scroll position when the active media
          // is changed by a product variant selection.
          if (prepend) return;
          
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (prepend) this.finishQuietVariantSwap(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      /**
       * L1 — capture outgoing featured media BEFORE hide_variants remount.
       * Called from product-info updateMedia prior to DOM add/remove.
       */
      beginQuietVariantSwap() {
        this.clearQuietVariantSwap();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const outgoingActive = this.elements.viewer.querySelector('.product__media-item.is-active');
        const fromImg =
          outgoingActive?.querySelector('.product__media img') ||
          outgoingActive?.querySelector('img');
        const host = this.elements.viewer;
        if (!outgoingActive || !fromImg || !host) return;

        const hostRect = host.getBoundingClientRect();
        const outRect = outgoingActive.getBoundingClientRect();
        if (hostRect.width < 8 || outRect.width < 8 || outRect.height < 8) return;

        const overlay = document.createElement('div');
        overlay.className = 'mf-l1-media-swap-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        // Cover only the outgoing featured frame (stacked shows multiple; don't mask the stack)
        overlay.style.top = `${outRect.top - hostRect.top + host.scrollTop}px`;
        overlay.style.left = `${outRect.left - hostRect.left + host.scrollLeft}px`;
        overlay.style.width = `${outRect.width}px`;
        overlay.style.height = `${outRect.height}px`;

        const clone = fromImg.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('loading');
        clone.decoding = 'sync';
        const fromCs = host.ownerDocument.defaultView.getComputedStyle(fromImg);
        clone.style.objectFit = fromCs.objectFit || 'contain';
        clone.style.objectPosition = fromCs.objectPosition || 'center';
        overlay.appendChild(clone);

        const style = host.ownerDocument.defaultView.getComputedStyle(host);
        if (style.position === 'static') host.classList.add('mf-l1-media-swap-host');
        host.appendChild(overlay);
        this._l1Swap = { host, overlay };
      }

      finishQuietVariantSwap(incomingActive) {
        const swap = this._l1Swap;
        if (!swap) return;

        const settle = () => {
          const { overlay } = swap;
          if (!overlay?.isConnected) {
            this.clearQuietVariantSwap();
            return;
          }
          overlay.classList.add('mf-l1-media-swap-overlay--out');
          const done = () => this.clearQuietVariantSwap();
          overlay.addEventListener('transitionend', done, { once: true });
          window.setTimeout(done, 280);
        };

        const img =
          incomingActive?.querySelector('.product__media img') ||
          incomingActive?.querySelector('img');
        if (img && !img.complete) {
          let settled = false;
          const once = () => {
            if (settled) return;
            settled = true;
            settle();
          };
          img.addEventListener('load', once, { once: true });
          img.addEventListener('error', once, { once: true });
          // Cap wait — must not feel like delayed variant selection
          window.setTimeout(once, 120);
        } else {
          requestAnimationFrame(() => requestAnimationFrame(settle));
        }
      }

      clearQuietVariantSwap() {
        const swap = this._l1Swap;
        this._l1Swap = null;
        if (!swap) return;
        swap.overlay?.remove();
        swap.host?.classList?.remove('mf-l1-media-swap-host');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
